/**
 * SubscriptionsPage — the cross-lab subscription directory + the authoritative plan write (MANAGEMENT 14).
 * Every lab × its current central entitlement projection, server-paginated + filtered, with a per-lab
 * plan-CHANGE action (PUT, reissue/supersede) and a CANCEL action (DELETE, soft-delete → ANNULEE). This is
 * the WRITE-side console: the projection the on-box entitlement gate consumes is set HERE; the lab box holds
 * a read-only cache. Visual + tone parity with the lab cockpit's read-only SubscriptionPage (subscriptionTone).
 *
 * AUTHORITY — the super-admin plane (`est_admin_plateforme`, re-checked LIVE server-side). The route already
 * rides <PlatformAdminGuard> (index.tsx); this screen ALSO renders its own <ForbiddenState> defensively and
 * short-circuits offline + the cloud-only "module not mounted on this install" (404/501) state. Full 5-state
 * DoD: forbidden / offline / cloud-absent / loading / error / empty + the live table. No PHI on this seam (L-4).
 */
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  FormField,
  Input,
  LoadingState,
  Modal,
  OfflineState,
  Select,
  Table,
  Textarea,
  useToast,
  type Column,
} from "@/ui";
import { ApiError } from "@/api";
import { useT, useUiStore } from "@/app";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { isManagementAbsent, useSubscriptions } from "../api";
import { useIsPlatformAdmin } from "../session";
import { formatDateTime, subscriptionTone } from "../lib";
import type {
  SubscriptionFilters,
  SubscriptionRow,
  SubscriptionStatut,
} from "../types";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { DistributionBar, StatChips, type DistributionSlice } from "../components/Distribution";
import { useCancelSubscription, useChangePlan } from "./subscriptionsApi";
import { registerSubscriptionsMessages } from "./subscriptionsMessages";

// Register the screen's i18n additively at module-eval (idempotent latch). The shell's onRegister() owns
// the base `mgmt.subscriptions.title`/`.subtitle`; this adds only the table + modal keys.
registerSubscriptionsMessages();

/** The full central statut vocabulary, in display order (mirrors the server SUBSCRIPTION_STATUTS). */
const ALL_STATUTS: ReadonlyArray<SubscriptionStatut> = [
  "ESSAI",
  "ACTIVE",
  "PAST_DUE",
  "SUSPENDUE",
  "EXPIREE",
  "ANNULEE",
];

/** A short, stable lab display label (commercial name → raison sociale → reference → id). */
function labLabel(row: SubscriptionRow): string {
  const l = row.laboratoire;
  return l.nomCommercial || l.raisonSociale || l.reference || `#${l.id}`;
}

export function SubscriptionsPage() {
  const { t, locale } = useT();
  const online = useUiStore((s) => s.online);
  const isAdmin = useIsPlatformAdmin();

  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState<SubscriptionStatut | "">("");
  // undefined = no modal; a row = its target. Separate cursors keep the two actions independent.
  const [changing, setChanging] = useState<SubscriptionRow | undefined>(undefined);
  const [cancelling, setCancelling] = useState<SubscriptionRow | undefined>(undefined);

  const filters: SubscriptionFilters = useMemo(
    () => ({
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(statut ? { statut } : {}),
      page: 1,
      pageSize: 100,
    }),
    [search, statut],
  );

  const enabled = isAdmin && online;
  const query = useSubscriptions(filters, enabled);
  const rows = useMemo(() => query.data?.items ?? [], [query.data]);

  // Per-page subscription synthesis (DERIVED from the loaded directory — schema-free, no extra read): the
  // statut distribution + the active / unsynced / cancelled headline counts. Aggregate counts only (never
  // echoes a lab identity). Refreshes in lock-step with the table.
  const statutSlices = useMemo<DistributionSlice[]>(() => {
    const counts = new Map<SubscriptionStatut | "_unsynced", number>();
    for (const r of rows) {
      const k = r.subscription.statut ?? "_unsynced";
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return ALL_STATUTS.filter((s) => (counts.get(s) ?? 0) > 0).map((s) => ({
      key: s,
      label: t(`mgmt.subs.statut.${s}`),
      value: counts.get(s) ?? 0,
      tone: subscriptionTone(s),
    }));
  }, [rows, t]);
  const activeCount = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.subscription.statut === "ESSAI" ||
          r.subscription.statut === "ACTIVE" ||
          r.subscription.statut === "PAST_DUE",
      ).length,
    [rows],
  );
  const unsyncedCount = useMemo(
    () => rows.filter((r) => r.subscription.statut === null).length,
    [rows],
  );
  const cancelledCount = useMemo(
    () => rows.filter((r) => r.subscription.statut === "ANNULEE").length,
    [rows],
  );

  const header = (
    <PageHeader title={t("mgmt.subscriptions.title")} subtitle={t("mgmt.subscriptions.subtitle")} />
  );

  if (!isAdmin) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={rows.length > 0} />
      </Screen>
    );
  }

  // Cloud-only module on a local box → its routes 404/501. Surface the clean "not available here" notice.
  if (isManagementAbsent(query.error)) {
    return (
      <Screen>
        {header}
        <Card title={t("mgmt.unavailable.title")}>
          <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>
            {t("mgmt.unavailable.desc")}
          </p>
        </Card>
      </Screen>
    );
  }

  if (query.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={6} />
      </Screen>
    );
  }
  if (query.isError) {
    return (
      <Screen>
        {header}
        <ErrorState
          message={query.error.message}
          code={query.error.code}
          onRetry={() => void query.refetch()}
        />
      </Screen>
    );
  }

  const statutLabel = (s: SubscriptionStatut | null): string =>
    s ? t(`mgmt.subs.statut.${s}`) : t("mgmt.subs.row.notSynced");

  const columns: ReadonlyArray<Column<SubscriptionRow>> = [
    {
      key: "lab",
      header: t("mgmt.subs.col.lab"),
      sortable: true,
      sortAccessor: (r) => labLabel(r).toLowerCase(),
      cell: (r) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{labLabel(r)}</span>
          {r.laboratoire.reference && (
            <span
              className="nv-ltr-island"
              style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}
            >
              {r.laboratoire.reference}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "deploy",
      header: t("mgmt.subs.col.deploy"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.laboratoire.deployClass ?? "",
      cell: (r) => (
        <span className="nv-ltr-island">{r.laboratoire.deployClass ?? "—"}</span>
      ),
    },
    {
      key: "plan",
      header: t("mgmt.subs.col.plan"),
      sortable: true,
      sortAccessor: (r) => r.subscription.plan ?? "",
      cell: (r) =>
        r.subscription.plan ? (
          <span>{r.subscription.plan}</span>
        ) : (
          <span style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>
            {t("mgmt.subs.row.noPlan")}
          </span>
        ),
    },
    {
      key: "statut",
      header: t("mgmt.subs.col.statut"),
      sortable: true,
      // Order by the canonical statut sequence (unsynced rows sort last).
      sortAccessor: (r) => {
        const i = r.subscription.statut ? ALL_STATUTS.indexOf(r.subscription.statut) : -1;
        return i < 0 ? ALL_STATUTS.length : i;
      },
      cell: (r) => (
        <Badge tone={subscriptionTone(r.subscription.statut)} glyph="●">
          {statutLabel(r.subscription.statut)}
        </Badge>
      ),
    },
    {
      key: "modules",
      header: t("mgmt.subs.col.modules"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.subscription.modulesCloud.length + r.subscription.modulesLocal.length,
      cell: (r) => (
        <span className="nv-ltr-island">
          {t("mgmt.subs.modules.count", {
            cloud: r.subscription.modulesCloud.length,
            local: r.subscription.modulesLocal.length,
          })}
        </span>
      ),
    },
    {
      key: "projectedAt",
      header: t("mgmt.subs.col.projectedAt"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.subscription.projectedAt ?? "",
      cell: (r) => (
        <span className="nv-ltr-island">{formatDateTime(r.subscription.projectedAt, locale)}</span>
      ),
    },
    {
      key: "actions",
      header: t("mgmt.subs.col.actions"),
      align: "end",
      cell: (r) => (
        <div style={{ display: "flex", gap: "var(--nv-s-2)", justifyContent: "flex-end" }}>
          <Button size="sm" variant="secondary" onClick={() => setChanging(r)}>
            {t("mgmt.subs.action.change")}
          </Button>
          {r.subscription.statut !== null && r.subscription.statut !== "ANNULEE" && (
            <Button size="sm" variant="ghost" onClick={() => setCancelling(r)}>
              {t("mgmt.subs.action.cancel")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const hasFilter = !!search.trim() || !!statut;

  return (
    <Screen>
      {header}

      {rows.length > 0 && (
        <Card title={t("mgmt.subs.insight.title")}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <StatChips
              chips={[
                { key: "total", label: t("mgmt.subs.insight.total"), value: rows.length },
                {
                  key: "active",
                  label: t("mgmt.subs.insight.active"),
                  value: activeCount,
                  tone: "success",
                },
                {
                  key: "cancelled",
                  label: t("mgmt.subs.insight.cancelled"),
                  value: cancelledCount,
                  tone: cancelledCount > 0 ? "danger" : "neutral",
                },
                {
                  key: "unsynced",
                  label: t("mgmt.subs.insight.unsynced"),
                  value: unsyncedCount,
                  tone: unsyncedCount > 0 ? "warn" : "neutral",
                },
              ]}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
              <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-text-2, var(--nv-slate))" }}>
                {t("mgmt.subs.insight.byStatut")}
              </span>
              <DistributionBar
                slices={statutSlices}
                emptyLabel={t("mgmt.subs.insight.empty")}
                ariaLabel={t("mgmt.subs.insight.byStatut.aria")}
              />
            </div>
          </div>
        </Card>
      )}

      <Card padded={false}>
        <div
          style={{
            display: "flex",
            gap: "var(--nv-s-3)",
            flexWrap: "wrap",
            padding: "var(--nv-s-3)",
            borderBottom: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
          }}
        >
          <div style={{ flex: "1 1 240px" }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("mgmt.subs.search")}
              aria-label={t("mgmt.subs.search")}
            />
          </div>
          <Select
            value={statut}
            onChange={(e) => setStatut(e.target.value as SubscriptionStatut | "")}
            aria-label={t("mgmt.subs.filter.statut")}
            options={[
              { value: "", label: t("mgmt.subs.filter.statut.all") },
              ...ALL_STATUTS.map((s) => ({ value: s, label: t(`mgmt.subs.statut.${s}`) })),
            ]}
          />
        </div>
        <div style={{ padding: "var(--nv-s-3)" }}>
          <Table
            columns={columns}
            rows={rows}
            rowKey={(r) => r.laboratoire.id}
            caption={t("mgmt.subscriptions.title")}
            pageSize={12}
            emptyTitle={
              hasFilter && search.trim() ? t("mgmt.subs.empty.search") : t("mgmt.subs.empty.title")
            }
            {...(hasFilter ? {} : { emptyDescription: t("mgmt.subs.empty.desc") })}
          />
        </div>
      </Card>

      <ChangePlanModal row={changing} onClose={() => setChanging(undefined)} />
      <CancelSubscriptionModal row={cancelling} onClose={() => setCancelling(undefined)} />
    </Screen>
  );
}

// ── Change-plan modal (PUT — reissue/supersede the projection) ───────────────────────
function ChangePlanModal({
  row,
  onClose,
}: {
  row: SubscriptionRow | undefined;
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const open = row !== undefined;
  // The mutation is keyed on the target lab id; "0" is an inert stand-in while the modal is closed.
  const change = useChangePlan(row?.laboratoire.id ?? "0");

  const [plan, setPlan] = useState("");
  const [statut, setStatut] = useState<SubscriptionStatut>("ACTIVE");
  const [modulesCloud, setModulesCloud] = useState("");
  const [modulesLocal, setModulesLocal] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Seed the form from the target row when the modal opens (one-shot per target).
  const seedKey = open ? row!.laboratoire.id : "_closed";
  const [seeded, setSeeded] = useState<string | null>(null);
  if (open && seeded !== seedKey) {
    setSeeded(seedKey);
    setPlan(row!.subscription.plan ?? "");
    setStatut(row!.subscription.statut ?? "ACTIVE");
    setModulesCloud(row!.subscription.modulesCloud.join(", "));
    setModulesLocal(row!.subscription.modulesLocal.join(", "));
    setReason("");
    setError(null);
  }
  if (!open && seeded !== null) setSeeded(null);

  // "a, b ,, c" → ["a","b","c"] (trim + drop blanks; the server stores the SKU list verbatim).
  const splitSkus = (raw: string): string[] =>
    raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

  const submit = () => {
    setError(null);
    if (!statut) {
      setError(t("mgmt.subs.modal.statutRequired"));
      return;
    }
    const cloud = splitSkus(modulesCloud);
    const local = splitSkus(modulesLocal);
    change.mutate(
      {
        plan: plan.trim() ? plan.trim() : null,
        statut,
        // The flat `modules` set is the union the gate reads; cloud/local are the split projections.
        modules: [...new Set([...cloud, ...local])],
        modules_cloud: cloud,
        modules_local: local,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("mgmt.subs.saved"));
          onClose();
        },
        onError: (err) =>
          setError(err instanceof ApiError ? err.message : t("mgmt.subs.error")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={open ? t("mgmt.subs.modal.title", { lab: labLabel(row!) }) : ""}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("mgmt.subs.modal.cancel")}
          </Button>
          <Button onClick={submit} loading={change.isPending}>
            {t("mgmt.subs.modal.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <FormField label={t("mgmt.subs.modal.statut")} required>
          {(p) => (
            <Select
              {...p}
              value={statut}
              onChange={(e) => setStatut(e.target.value as SubscriptionStatut)}
              options={ALL_STATUTS.map((s) => ({ value: s, label: t(`mgmt.subs.statut.${s}`) }))}
            />
          )}
        </FormField>
        <FormField label={t("mgmt.subs.modal.plan")}>
          {(p) => (
            <Input
              {...p}
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              placeholder={t("mgmt.subs.modal.plan.placeholder")}
            />
          )}
        </FormField>
        <FormField label={t("mgmt.subs.modal.modulesCloud")} hint={t("mgmt.subs.modal.modulesCloud.hint")}>
          {(p) => (
            <Input
              {...p}
              value={modulesCloud}
              onChange={(e) => setModulesCloud(e.target.value)}
              className="nv-ltr-island"
            />
          )}
        </FormField>
        <FormField label={t("mgmt.subs.modal.modulesLocal")} hint={t("mgmt.subs.modal.modulesLocal.hint")}>
          {(p) => (
            <Input
              {...p}
              value={modulesLocal}
              onChange={(e) => setModulesLocal(e.target.value)}
              className="nv-ltr-island"
            />
          )}
        </FormField>
        <FormField label={t("mgmt.subs.modal.reason")} hint={t("mgmt.subs.modal.reason.hint")}>
          {(p) => <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />}
        </FormField>
        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ── Cancel modal (DELETE — soft-delete → ANNULEE) ────────────────────────────────────
function CancelSubscriptionModal({
  row,
  onClose,
}: {
  row: SubscriptionRow | undefined;
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const open = row !== undefined;
  const cancel = useCancelSubscription(row?.laboratoire.id ?? "0");

  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const seedKey = open ? row!.laboratoire.id : "_closed";
  const [seeded, setSeeded] = useState<string | null>(null);
  if (open && seeded !== seedKey) {
    setSeeded(seedKey);
    setReason("");
    setError(null);
  }
  if (!open && seeded !== null) setSeeded(null);

  const submit = () => {
    setError(null);
    cancel.mutate(
      { ...(reason.trim() ? { reason: reason.trim() } : {}) },
      {
        onSuccess: () => {
          toast.success(t("mgmt.subs.cancelled"));
          onClose();
        },
        onError: (err) =>
          setError(err instanceof ApiError ? err.message : t("mgmt.subs.error")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={open ? t("mgmt.subs.cancel.title", { lab: labLabel(row!) }) : ""}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("mgmt.subs.modal.cancel")}
          </Button>
          <Button onClick={submit} loading={cancel.isPending}>
            {t("mgmt.subs.cancel.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>
          {t("mgmt.subs.cancel.warn")}
        </p>
        <FormField label={t("mgmt.subs.cancel.reason")}>
          {(p) => <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />}
        </FormField>
        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
